import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowClockwise, CircleNotch, FilmReel, LinkSimple, Trash, UploadSimple } from '@phosphor-icons/react';
import * as Tabs from '@radix-ui/react-tabs';
import { uploadFile, type UploadPurpose } from '@/lib/api/uploadFile';
import { cn } from '@/lib/utils/cn';
import { inputClasses } from '../sections/shared';

interface Props {
  kind: 'image' | 'video';
  value: string;
  onChange: (next: string) => void;
  purpose: UploadPurpose;
  maxBytes?: number;
  className?: string;
}

const DEFAULT_MAX = 6 * 1024 * 1024;

export function MediaInput({
  kind,
  value,
  onChange,
  purpose,
  maxBytes = DEFAULT_MAX,
  className,
}: Props) {
  const [tab, setTab] = useState<'upload' | 'url'>(value && /^https?:\/\//.test(value) ? 'url' : 'upload');

  if (value) {
    return (
      <div className={cn('space-y-2', className)}>
        <MediaPreview kind={kind} url={value} />
        <div className="flex gap-2">
          <FilePickerButton
            kind={kind}
            purpose={purpose}
            maxBytes={maxBytes}
            onUpload={onChange}
            label="Replace"
            icon={<ArrowClockwise size={12} />}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-red-300 hover:text-red-700"
          >
            <Trash size={12} /> Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <Tabs.Root
      value={tab}
      onValueChange={(v) => setTab(v as 'upload' | 'url')}
      className={className}
    >
      <Tabs.List className="mb-3 inline-flex rounded-md border border-line bg-board p-0.5 text-xs">
        <Tabs.Trigger
          value="upload"
          className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium text-ink-muted data-[state=active]:bg-rail data-[state=active]:text-ink"
        >
          <UploadSimple size={12} /> Upload
        </Tabs.Trigger>
        <Tabs.Trigger
          value="url"
          className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium text-ink-muted data-[state=active]:bg-rail data-[state=active]:text-ink"
        >
          <LinkSimple size={12} /> URL
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="upload">
        <Dropzone kind={kind} purpose={purpose} maxBytes={maxBytes} onUpload={onChange} />
      </Tabs.Content>
      <Tabs.Content value="url">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={kind === 'image' ? 'https://…/cover.jpg' : 'https://youtube.com/watch?v=…'}
          className={inputClasses}
        />
        <p className="mt-1.5 text-xs text-ink-muted">
          {kind === 'image'
            ? 'Direct link to a PNG, JPG, or WebP.'
            : 'MP4, YouTube, or Vimeo URL.'}
        </p>
      </Tabs.Content>
    </Tabs.Root>
  );
}

function FilePickerButton({
  kind,
  purpose,
  maxBytes,
  onUpload,
  label,
  icon,
}: {
  kind: 'image' | 'video';
  purpose: UploadPurpose;
  maxBytes: number;
  onUpload: (url: string) => void;
  label: string;
  icon?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File) {
    if (file.size > maxBytes) {
      toast.error('File too large', { description: `Max ${Math.round(maxBytes / 1024 / 1024)} MB.` });
      return;
    }
    setBusy(true);
    try {
      const { url } = await uploadFile(file, purpose);
      onUpload(url);
    } catch (e) {
      toast.error('Upload failed', {
        description: e instanceof Error ? e.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={kind === 'image' ? 'image/png,image/jpeg,image/webp' : 'video/mp4,video/webm'}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail disabled:opacity-60"
      >
        {busy ? <CircleNotch size={12} className="animate-spin" /> : icon}
        {busy ? 'Uploading…' : label}
      </button>
    </>
  );
}

function Dropzone({
  kind,
  purpose,
  maxBytes,
  onUpload,
}: {
  kind: 'image' | 'video';
  purpose: UploadPurpose;
  maxBytes: number;
  onUpload: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File) {
    if (file.size > maxBytes) {
      toast.error('File too large', { description: `Max ${Math.round(maxBytes / 1024 / 1024)} MB.` });
      return;
    }
    const accepted = kind === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/');
    if (!accepted) {
      toast.error('Wrong file type', { description: `Expected a${kind === 'image' ? 'n image' : ' video'}.` });
      return;
    }
    setBusy(true);
    try {
      const { url } = await uploadFile(file, purpose);
      onUpload(url);
    } catch (e) {
      toast.error('Upload failed', {
        description: e instanceof Error ? e.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-board py-10 text-center transition-colors',
        dragging ? 'border-ink bg-rail/60' : 'border-line hover:border-ink',
        busy && 'pointer-events-none opacity-60',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={kind === 'image' ? 'image/png,image/jpeg,image/webp' : 'video/mp4,video/webm'}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-canvas text-ink-muted">
        {busy ? <CircleNotch size={16} className="animate-spin" /> : kind === 'image' ? <UploadSimple size={16} /> : <FilmReel size={16} />}
      </span>
      <p className="text-sm font-medium text-ink">
        {busy ? 'Uploading…' : dragging ? 'Drop to upload' : `Drag ${kind} here, or click to browse`}
      </p>
      <p className="text-[11px] text-ink-faint">
        {kind === 'image' ? 'PNG, JPG, WebP' : 'MP4, WebM'} · Max{' '}
        {Math.round(maxBytes / 1024 / 1024)} MB
      </p>
    </div>
  );
}

function MediaPreview({ kind, url }: { kind: 'image' | 'video'; url: string }) {
  if (kind === 'image') {
    return (
      <div className="overflow-hidden rounded-lg border border-line bg-rail">
        <img src={url} alt="" className="aspect-[16/10] w-full object-cover" />
      </div>
    );
  }
  const isFile = url.startsWith('blob:') || /\.(mp4|webm)(\?|$)/i.test(url);
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-rail">
      {isFile ? (
        <video src={url} controls className="aspect-[16/10] w-full bg-ink/90" />
      ) : (
        <div className="grid aspect-[16/10] w-full place-items-center text-xs text-ink-muted">
          <div className="text-center">
            <FilmReel size={20} className="mx-auto mb-1 text-ink-faint" />
            <span className="font-mono text-[11px] break-all px-4">{url}</span>
          </div>
        </div>
      )}
    </div>
  );
}
